import type { SearchOnMapResult } from '@/hooks'
import type { SearchAssetOnMapType } from '../types'

import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react'

import { useIsUnmounted, useSearchOnMap } from '@/hooks'
import { updateLocationPin } from '@/map'
import { debounce } from '@/utils'

type SearchOnMapItemsOptions = {
  queryDelay?: number
  snapToRoad?: boolean
  ignoreMapBounds?: boolean
  queryMinLength: number
  assetTypes?: SearchAssetOnMapType[]
  pinId: uui.domain.ui.map.LocationPinId
}

export function useSearchOnMapItems(
  options: SearchOnMapItemsOptions,
  setLoading: Dispatch<SetStateAction<boolean>>,
) {
  const { pinId, assetTypes, snapToRoad, queryDelay, ignoreMapBounds, queryMinLength } = options

  const isUnmounted = useIsUnmounted()

  const [assets, setAssets] = useState<uui.domain.client.gps.SearchOnMapItem[]>([])

  const searchOnMapTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(
    () => () => {
      if (searchOnMapTimeoutIdRef.current) {
        clearTimeout(searchOnMapTimeoutIdRef.current)
      }
    },
    [],
  )

  const searchOnMap = useSearchOnMap({
    snapToRoad,
    assetTypes,
    ignoreMapBounds,
    delay: queryDelay,
    minLength: queryMinLength,
    excludeOutOfTerritoryItems: !!snapToRoad,
  })

  const clearAssetList = useCallback(() => void setAssets([]), [])

  const onTextChange = useCallback(
    (address: string) => {
      if (isUnmounted()) return

      setLoading(true)

      // set loading status to true
      updateLocationPin(pinId, { loading: true })

      // searchOnMap return a timeout ID, we clear it when the component unmounts
      searchOnMapTimeoutIdRef.current = searchOnMap(address, (result: SearchOnMapResult) => {
        if (isUnmounted()) return

        setAssets(result.items)

        if (result.requestStatus !== 'pending') {
          // set loading status to true
          updateLocationPin(pinId, { loading: false })
          setLoading(false)
        }
      })
    },
    [searchOnMap, setLoading, pinId, isUnmounted],
  )

  const debouncedTextChange = debounce(onTextChange, 350)

  return { assets, onTextChange: debouncedTextChange, clearAssetList }
}
